import { useQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  CheckCircle2Icon,
  MailIcon,
  ShieldIcon,
  UserIcon,
  XCircleIcon,
} from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/avatar/avatar';
import { Badge } from '@/components/badge/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Skeleton } from '@/components/skeleton/skeleton';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { getAdminQueryOptions } from '@/features/admins/api/get-admin.api';
import { getRoleQueryOptions } from '@/features/roles/api/get-role.api';
import type { Admin } from '@/features/admins/types/admin.type';

const dateFormatter = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(iso: string | null | undefined) {
  if (!iso) return '-';
  return dateFormatter.format(new Date(iso));
}

function getInitials(name: string | null | undefined) {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export interface DetailAdminDialogProps extends BaseDialogProps {
  adminId: string;
}

export default function DetailAdminDialog({
  dialog,
  adminId,
}: DetailAdminDialogProps) {
  const { closeDetailAdminDialog } = useV2DialogContext();

  const getAdminQuery = useQuery(getAdminQueryOptions(adminId));
  const admin = getAdminQuery.data?.result;

  const getRoleQuery = useQuery({
    ...getRoleQueryOptions({ roleId: admin?.role_id ?? undefined }),
    enabled: !!admin?.role_id,
  });
  const role = getRoleQuery.data?.result;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeDetailAdminDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Detail Admin</DialogTitle>
          <DialogDescription>
            Informasi lengkap tentang admin ini.
          </DialogDescription>
        </DialogHeader>

        {getAdminQuery.isPending ? (
          <DetailAdminSkeleton />
        ) : getAdminQuery.isError || !admin ? (
          <p className="text-sm text-destructive">
            Gagal memuat data admin. Silakan coba lagi.
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            <DetailAdminHeader admin={admin} />
            <DetailAdminInfoGrid
              admin={admin}
              roleName={role?.role}
              isRoleLoading={getRoleQuery.isPending && !!admin.role_id}
            />
            <DetailAdminMeta admin={admin} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DetailAdminHeaderProps {
  admin: Admin;
}

function DetailAdminHeader({ admin }: DetailAdminHeaderProps) {
  return (
    <div className="flex items-start gap-4">
      <Avatar size="lg" className="size-16">
        <AvatarFallback className="text-base">
          {getInitials(admin.admin_name)}
        </AvatarFallback>
      </Avatar>
      <div className="flex flex-1 flex-col gap-1.5">
        <p className="text-base font-semibold text-foreground">
          {admin.admin_name}
        </p>
        <p className="text-xs text-muted-foreground">{admin.admin_email}</p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <Badge variant={admin.is_verified ? 'info' : 'neutral'}>
            {admin.is_verified ? (
              <CheckCircle2Icon className="size-3" />
            ) : (
              <XCircleIcon className="size-3" />
            )}
            {admin.is_verified ? 'Terverifikasi' : 'Belum verifikasi'}
          </Badge>
        </div>
      </div>
    </div>
  );
}

interface DetailAdminInfoGridProps {
  admin: Admin;
  roleName?: string;
  isRoleLoading?: boolean;
}

function DetailAdminInfoGrid({
  admin,
  roleName,
  isRoleLoading,
}: DetailAdminInfoGridProps) {
  return (
    <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-muted/30 p-4 sm:grid-cols-2">
      <InfoRow
        icon={<UserIcon className="size-4" />}
        label="Nama"
        value={admin.admin_name}
      />
      <InfoRow
        icon={<MailIcon className="size-4" />}
        label="Email"
        value={admin.admin_email}
      />
      <InfoRow
        icon={<ShieldIcon className="size-4" />}
        label="Role"
        value={
          !admin.role_id ? '-' : isRoleLoading ? 'Memuat...' : (roleName ?? '-')
        }
      />
    </div>
  );
}

interface DetailAdminMetaProps {
  admin: Admin;
}

function DetailAdminMeta({ admin }: DetailAdminMetaProps) {
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Dibuat Pada"
        value={formatDate(admin.created_at)}
      />
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Diperbarui Pada"
        value={formatDate(admin.updated_at)}
      />
    </div>
  );
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="wrap-break-word text-sm font-medium text-foreground">
          {value}
        </p>
      </div>
    </div>
  );
}

function DetailAdminSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start gap-4">
        <Skeleton className="size-16 rounded-full" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-56" />
          <div className="mt-1 flex gap-2">
            <Skeleton className="h-5 w-24 rounded-full" />
          </div>
        </div>
      </div>
      <Skeleton className="h-32 w-full rounded-lg" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}
